import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Typography, Input, Avatar, Button, Tooltip, Space, theme, Modal, Spinner, Select, Popover, FilterResetButton, Image, message, Slider, Upload, Table, Dropdown, MenuItem, MenuDivider, MenuLabel, StatCard } from '../../ui';
import { Plus, Pencil, Eye, Trash2, QrCode, GitBranch, Filter, Upload as UploadIcon, Download, ArrowUpDown, FileUp, FileSpreadsheet, FileJson, Boxes, PackageX, IndianRupee } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUrlList, useUrlString, useUrlNumberRange } from '../../hooks/useUrlState';
import { request } from '../../api/client';
import { usePermissionChecker, useCanSeeWarehouses } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { fmtPackQty, fmtUom } from '../../lib/format';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import { ProductFormModal } from './ProductFormModal';
import { AddVariantModal } from './AddVariantModal';
import { ProductDetailModal } from './ProductDetailModal';
import { BulkImportModal } from '../../components/BulkImportModal';
import { downloadCsv } from '../../lib/downloadCsv';
import { ExportDialog } from '../../components/ExportDialog';
import { downloadJson } from '../../lib/downloadJson';
import { toImageUrls } from '../../lib/imageUrls';
import { MasterTag } from '../../lib/warehouseLabel';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const NUM = { fontVariantNumeric: 'tabular-nums' as const };
const UNIT_ABBR: Record<string, string> = { KG: 'kg', LTR: 'L', NOS: 'ea', BAG: 'bag', BOX: 'box' };
// Per-unit price (₹/kg, ₹/L…) — only meaningful for multi/fractional packs; skip plain 1-unit packs.
const perUnit = (value: any, packSize: any, uom: string): string | null => {
  const ps = Number(packSize);
  const v = Number(value);
  if (!Number.isFinite(v) || !(ps > 0) || ps === 1) return null;
  return `${inr(v / ps)} /${UNIT_ABBR[uom] ?? (uom ? uom.toLowerCase() : 'unit')}`;
};

export function ProductsListPage() {
  const canSeeWarehouses = useCanSeeWarehouses();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  // Backed by ?search= so a ProductLink elsewhere in the app lands on the right row instead of an
  // unfiltered catalogue — and so the term survives a trip into a product and back.
  const [searchInput, setSearchInput] = useUrlString('search');
  const search = useDebounce(searchInput, 400);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [importOpen, setImportOpen] = useState(false);
  const hasPerm = usePermissionChecker();
  const canCreate = hasPerm('PRODUCT.CREATE');
  // PRODUCT.VIEW is held by RETAILER and FARMER too (catalogue + complaint form), so it must NOT gate an
  // export that carries dealer price. Matches the server's PRODUCT.EXPORT gate.
  const canExport = hasPerm('PRODUCT.EXPORT');
  const canEdit = hasPerm('PRODUCT.EDIT');
  const canDelete = hasPerm('PRODUCT.DELETE');
  const canQr = hasPerm('PRODUCT.QR.GENERATE');
  const canValuation = hasPerm('STOCK.VALUATION.VIEW');
  const qc = useQueryClient();
  const [qrFor, setQrFor] = useState<any>(null);
  const [variantFor, setVariantFor] = useState<any>(null);
  const [viewFor, setViewFor] = useState<any>(null);

  // ---- Import/export hubs: CSV (base data) + JSON (rich detail content) ----
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [detailsImportOpen, setDetailsImportOpen] = useState(false);
  const [detailsFile, setDetailsFile] = useState<File | null>(null);
  const [pendingDetails, setPendingDetails] = useState<any[] | null>(null);
  const [importingDetails, setImportingDetails] = useState(false);
  const resetDetailsImport = () => { setDetailsImportOpen(false); setDetailsFile(null); setPendingDetails(null); };
  const parseDetailsFile = async (file: File) => {
    setDetailsFile(file);
    try {
      const parsed = JSON.parse(await file.text());
      const items = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.items) ? parsed.items : [];
      if (!items.length) throw new Error('no product-detail records found');
      setPendingDetails(items);
    } catch (err: any) {
      message.error(`Invalid JSON file: ${err.message}`);
      setPendingDetails(null);
    }
  };
  const doDetailsImport = async () => {
    if (!pendingDetails?.length) return;
    setImportingDetails(true);
    try {
      const res = await request<{ imported: number; skipped: { groupCode: string; reason: string }[] }>({
        url: '/products/details/import',
        method: 'POST',
        data: { items: pendingDetails },
      });
      const skip = res.skipped?.length ? `, skipped ${res.skipped.length}` : '';
      message.success(`Imported ${res.imported} product master(s)${skip}`);
      qc.invalidateQueries({ queryKey: ['catalog-with-stock'] });
      resetDetailsImport();
    } catch (err: any) {
      message.error(err?.response?.data?.error?.message ?? 'Import failed');
    } finally {
      setImportingDetails(false);
    }
  };

  // ---- Filters ----
  const [groupFilter, setGroupFilter] = useUrlList('group');
  const [uomFilter, setUomFilter] = useUrlList('uom');
  const [whFilter, setWhFilter] = useUrlList('wh');
  const [stockFilter, setStockFilter] = useUrlString<'all' | 'in' | 'low' | 'out'>('stock', 'all');
  const [priceRange, setPriceRange] = useUrlNumberRange('price');
  const [typeFilter, setTypeFilter] = useUrlString<'all' | 'single' | 'multi'>('type', 'all');
  const [statusFilter, setStatusFilter] = useUrlString<'true' | 'false' | 'all'>('status', 'true');  // active | inactive | all

  // Server-side filtering: each active filter becomes a query param so the backend
  // returns ONLY matching rows. Option lists come from the facets endpoint below.
  const params = useMemo(() => {
    const p: Record<string, string | number> = {};
    if (search.trim()) p.q = search.trim();
    if (groupFilter.length) p.group = groupFilter.join(',');
    if (uomFilter.length) p.uom = uomFilter.join(',');
    if (whFilter.length) p.warehouse = whFilter.join(',');
    if (stockFilter !== 'all') p.stock = stockFilter;
    if (typeFilter !== 'all') p.type = typeFilter;
    if (statusFilter !== 'true') p.active = statusFilter;
    if (priceRange) { p.priceMin = priceRange[0]; p.priceMax = priceRange[1]; }
    return p;
  }, [search, groupFilter, uomFilter, whFilter, stockFilter, typeFilter, statusFilter, priceRange]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['catalog-with-stock', params],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock', params }),
    placeholderData: keepPreviousData,
  });

  // Facets + summary stats over the FULL active catalog (independent of current filters):
  // categories/UoMs/warehouses that actually exist in products, price bounds, and tile counts.
  const { data: facets } = useQuery({
    queryKey: ['catalog-facets', statusFilter],
    queryFn: () => request<any>({ url: '/catalog/filter-options', params: statusFilter !== 'true' ? { active: statusFilter } : {} }),
  });
  // Master warehouse codes for the filter capsule — best-effort (degrades to no capsule without WAREHOUSE.VIEW).
  // The facets warehouse options are keyed by code, so we match on code.
  const { data: whAll } = useQuery({ queryKey: ['warehouses-all'], queryFn: () => request<any[]>({ url: '/warehouses/my' }), enabled: canSeeWarehouses, staleTime: 5 * 60_000, retry: false });
  const masterCodes = useMemo(() => new Set((whAll ?? []).filter((w: any) => w.isMaster).map((w: any) => String(w.code))), [whAll]);

  // Total on-hand stock value (at cost) across all warehouses — for the valuation KPI.
  const { data: valuation } = useQuery({
    queryKey: ['stock-valuation-total'],
    queryFn: () => request<{ totalValue: number }>({ url: '/stock/valuation' }),
    enabled: canValuation,
  });

  const del = useMutation({
    mutationFn: (id: string) => request({ url: `/products/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Product archived');
      qc.invalidateQueries({ queryKey: ['catalog-with-stock'] });
      qc.invalidateQueries({ queryKey: ['catalog-facets'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to archive'),
  });

  // QR is fetched on demand when a row's QR button is clicked.
  const { data: qr, isFetching: qrLoading } = useQuery({
    queryKey: ['product-qr', qrFor?.productId],
    queryFn: () => request<{ text: string; dataUrl: string }>({ url: `/products/${qrFor.productId}/qr` }),
    enabled: !!qrFor,
  });

  // Answers read No / "Yes, archive it" like every other confirmation in the app — a destructive answer
  // should never be a guess about which button means which (DEF-081).
  const confirmDelete = (r: any) =>
    Modal.confirm({
      title: `Archive ${r.sku}?`,
      content: `"${r.name}" will be deactivated and removed from the active catalog. You can re-activate it later by editing it.`,
      okText: 'Yes, archive it',
      cancelText: 'No',
      okButtonProps: { danger: true },
      onOk: () => del.mutateAsync(r.productId),
    });

  // Rows arrive already filtered by the server; the client only groups them for display.
  const rows = data ?? [];

  // Option lists + price bounds come from facets (only group codes/units/warehouses present in products).
  const groupOptions = facets?.groups ?? [];
  const uomOptions = facets?.uoms ?? [];
  const whOptions = facets?.warehouses ?? [];
  const priceBounds: [number, number] = [facets?.priceMin ?? 0, facets?.priceMax ?? 0];
  const hasPriceFilter = priceBounds[1] > priceBounds[0];

  const activeFilters =
    (search ? 1 : 0) + (groupFilter.length ? 1 : 0) + (uomFilter.length ? 1 : 0) + (whFilter.length ? 1 : 0) +
    (stockFilter !== 'all' ? 1 : 0) + (priceRange ? 1 : 0) + (typeFilter !== 'all' ? 1 : 0) + (statusFilter !== 'true' ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setGroupFilter([]); setUomFilter([]); setWhFilter([]);
    setStockFilter('all'); setPriceRange(null); setTypeFilter('all'); setStatusFilter('true');
  };

  // Collapse variants of the same product (group_code) into ONE expandable master
  // row; single-pack products stay as a normal row.
  const groupedRows = useMemo(() => {
    const byKey = new Map<string, any[]>();
    for (const r of rows) {
      const key = (r.groupCode && String(r.groupCode).trim()) || r.sku;
      const arr = byKey.get(key);
      if (arr) arr.push(r); else byKey.set(key, [r]);
    }
    const out: any[] = [];
    byKey.forEach((vs, key) => {
      if (vs.length === 1) { out.push(vs[0]); return; }
      const mrps = vs.map((v) => Number(v.mrp)).filter(Number.isFinite);
      const prices = vs.map((v) => Number(v.price)).filter(Number.isFinite);
      out.push({
        _isGroup: true,
        productId: `group:${key}`,   // unique row key
        id: vs[0].productId,         // a real variant id, used by the "Add variant" action
        sku: key,
        groupCode: vs[0].groupCode,
        name: vs[0].name,
        uom: vs[0].uom,
        imageUrl: vs[0].imageUrl,
        _count: vs.length,
        _mrpMin: Math.min(...mrps), _mrpMax: Math.max(...mrps),
        _priceMin: Math.min(...prices), _priceMax: Math.max(...prices),
        // representative values so column sorting on a master row is sensible
        mrp: mrps.length ? Math.min(...mrps) : undefined,
        price: prices.length ? Math.min(...prices) : undefined,
        _stockTotal: vs.reduce((s, v) => s + (v.stock?.total ?? 0), 0),
        _allInactive: vs.every((v) => v.isActive === false),
        // Variants are shown in a self-headed sub-table when the master row is expanded (NOT as
        // column-reusing child rows), so they read as a distinct sub-section — see variantColumns.
        _variants: vs,
      });
    });
    return out;
  }, [rows]);
  const fmtPriceRange = (a: number, b: number) =>
    (!Number.isFinite(a) ? '—' : a === b ? inr(a) : `${inr(a)} – ${inr(b)}`);
  /**
   * The stock TOTAL, always marked with a Σ.
   *
   * The Σ used to be opt-in and was only passed on a group row, on the reasoning that it meant
   * "added up from variants you cannot see". But every number this renders is a total: on a leaf row
   * it is the sum of the warehouse pills sitting immediately to its left (230 + 551 + 431 = 1212).
   * Bare, it read as one more warehouse chip that had lost its label — the Σ is what separates a
   * total from the per-warehouse figures beside it, which is needed most exactly where the
   * breakdown IS shown.
   */
  const stockTag = (t: number) =>
    <Pill color={t === 0 ? 'red' : t < 100 ? 'gold' : 'green'}>Σ {t}</Pill>;

  // Tile counts reflect the FULL catalog (from facets), not the current filtered view.
  const totalSkus = facets?.totalSkus ?? 0;
  const outOfStock = facets?.outOfStock ?? 0;

  const columns: DataTableColumn<any>[] = [
    {
      key: 'image', title: '', dataIndex: 'imageUrl', width: 84, alwaysVisible: true,
      render: (url: string, r: any) => {
        const size = 48;
        // Gallery (if any) so the preview can page through all of a product's images.
        const normalized = toImageUrls(r.imageUrls);
        const gallery: string[] = (normalized.length ? normalized : (url ? [url] : []));
        if (!url) {
          return (
            <Avatar size={size} className="rounded-md! text-white!" style={{ background: 'var(--color-brand-600)', flexShrink: 0 }} fallback={r.name?.[0]?.toUpperCase()} />
          );
        }
        // Click opens a full-screen preview (zoom/rotate); stop propagation so it
        // doesn't also expand/select the row.
        return (
          <span onClick={(e) => e.stopPropagation()} style={{ cursor: 'zoom-in' }}>
            <Image.PreviewGroup>
              <Image
                src={url}
                width={size}
                height={size}
                style={{ objectFit: 'cover', borderRadius: 6 }}
                preview
              />
            </Image.PreviewGroup>
          </span>
        );
      },
    },
    {
      // Widthless, this column soaked up ALL the leftover table width — every other column here is
      // pinned, so Product stretched to ~700px of mostly blank space between a short product name and
      // the SKU, while Stock sat capped at a width its own chips could not fit on one line. Capped so
      // that surplus is shared out instead of pooling in the one unpinned column.
      key: 'name', title: 'Product', dataIndex: 'name', sortable: true, alwaysVisible: true, width: 260,
      render: (v: string, r: any) => {
        if (r._isGroup) {
          return (
            <div style={{ lineHeight: 1.3 }}>
              <Space size={6} wrap={false}>
                <b style={{ fontSize: 14 }}>{v}</b>
                <Pill color="green">{r._count} variants</Pill>
                {r._allInactive && <Pill>Inactive</Pill>}
              </Space>
              <div style={{ fontSize: 11, color: token.colorTextSecondary }}>{r._count} packs · {r.uom}</div>
            </div>
          );
        }
        // Standalone single product — show the NAME (fixes the "shows SKU" bug); SKU has its own column.
        return (
          <div style={{ lineHeight: 1.3 }}>
            <Space size={6} wrap={false}>
              <b style={{ fontSize: 14 }}>{v}</b>
              {r.isActive === false && <Pill>Inactive</Pill>}
            </Space>
            {r.variantLabel && (
              <div style={{ fontSize: 11, color: token.colorTextSecondary }}>{r.variantLabel}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'sku', title: 'SKU', dataIndex: 'sku', width: 140, sortable: true,
      render: (v: string, r: any) =>
        r._isGroup
          ? <span style={{ fontFamily: MONO, fontSize: 12, color: token.colorTextSecondary }}>{r.groupCode || '—'}</span>
          : <span style={{ fontFamily: MONO, fontSize: 13 }}>{v}</span>,
    },
    {
      key: 'pack', title: 'Pack', dataIndex: 'packSize', width: 88,
      render: (_: any, r: any) =>
        r._isGroup
          ? <span style={{ color: token.colorTextSecondary }}>{r.uom}</span>
          : <span style={NUM}>{Number(r.packSize) > 0 ? fmtUom(r.packSize, r.uom) : r.uom}</span>,
    },
    {
      key: 'mrp', title: 'MRP', dataIndex: 'mrp', width: 118, sortable: true, align: 'right',
      render: (v: any, r: any) =>
        r._isGroup
          ? <span style={{ ...NUM, color: token.colorTextSecondary }}>{fmtPriceRange(r._mrpMin, r._mrpMax)}</span>
          : (
            <div style={{ ...NUM, color: token.colorTextSecondary, lineHeight: 1.25 }}>
              {inr(v)}
              {perUnit(v, r.packSize, r.uom) && (
                <div style={{ fontSize: 11, color: token.colorTextTertiary }}>{perUnit(v, r.packSize, r.uom)}</div>
              )}
            </div>
          ),
    },
    {
      key: 'price', title: 'Dealer Price', dataIndex: 'price', width: 128, sortable: true, align: 'right',
      render: (v: any, r: any) =>
        r._isGroup
          ? <b style={NUM}>{fmtPriceRange(r._priceMin, r._priceMax)}</b>
          : (
            <div style={{ ...NUM, lineHeight: 1.25 }}>
              <b>{inr(v)}</b>
              {perUnit(v, r.packSize, r.uom) && (
                <div style={{ fontSize: 11, fontWeight: 400, color: token.colorTextTertiary }}>{perUnit(v, r.packSize, r.uom)}</div>
              )}
            </div>
          ),
    },
    {
      // Fits the widest cell this column can hold ON ONE LINE: three warehouse pills, a "+N" pill for
      // the rest, and the total tag. At 380 that set wrapped, dropping the total onto a second row.
      key: 'stock', title: 'Stock', dataIndex: 'stock', width: 520,
      sorter: (a: any, b: any) =>
        ((a._isGroup ? a._stockTotal : a.stock?.total) ?? 0) - ((b._isGroup ? b._stockTotal : b.stock?.total) ?? 0),
      render: (s: any, r: any) => {
        if (r._isGroup) {
          return <Tooltip label="Total stock across all variants">{stockTag(r._stockTotal)}</Tooltip>;
        }
        const whs = Object.entries(s?.warehouses ?? {});
        return (
          <span style={NUM} className="inline-flex flex-wrap items-center gap-1">
            {whs.slice(0, 3).map(([wh, q]) => (
              <Pill key={wh}>{wh}: {String(q)}</Pill>
            ))}
            {whs.length > 3 && <Pill>+{whs.length - 3}</Pill>}
            <Tooltip label={`${fmtPackQty(s?.total ?? 0, r.packSize, r.uom)} in stock`}><span className="inline-flex">{stockTag(s?.total ?? 0)}</span></Tooltip>
          </span>
        );
      },
    },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 188,
      render: (_: any, r: any) => (
        <Space wrap size="small" onClick={(e) => e.stopPropagation()}>
          {r._isGroup ? (
            <>
              {canEdit && (
                <Tooltip label="Edit product (shared by all variants)">
                  <Button variant="text" size="sm" icon={<Pencil size={16} />} aria-label="Edit product" onClick={() => { setEditing({ ...r, _editMode: 'master' }); setFormOpen(true); }} />
                </Tooltip>
              )}
              <Tooltip label="View details">
                <Button variant="text" size="sm" icon={<Eye size={16} />} aria-label="View details" onClick={() => setViewFor(r)} />
              </Tooltip>
              {canCreate && (
                <Tooltip label="Add variant">
                  <Button variant="text" size="sm" icon={<GitBranch size={16} />} aria-label="Add variant" onClick={() => setVariantFor(r)} />
                </Tooltip>
              )}
            </>
          ) : (
            <>
              {canEdit && (
                <Tooltip label={r._isChild ? 'Edit variant (this pack only)' : 'Edit'}>
                  <Button variant="text" size="sm" icon={<Pencil size={16} />} aria-label="Edit" onClick={() => { setEditing({ ...r, _editMode: r._isChild ? 'variant' : 'full' }); setFormOpen(true); }} />
                </Tooltip>
              )}
              {canCreate && (
                <Tooltip label="Add variant (link to this product)">
                  <Button variant="text" size="sm" icon={<GitBranch size={16} />} aria-label="Add variant" onClick={() => setVariantFor(r)} />
                </Tooltip>
              )}
              {canQr && (
                <Tooltip label="Generate QR">
                  <Button variant="text" size="sm" icon={<QrCode size={16} />} aria-label="Generate QR" onClick={() => setQrFor(r)} />
                </Tooltip>
              )}
              {!r._isChild && (
                <Tooltip label="View details">
                  <Button variant="text" size="sm" icon={<Eye size={16} />} aria-label="View details" onClick={() => setViewFor(r)} />
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip label="Delete (archive)">
                  <Button variant="text" size="sm" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} aria-label="Delete (archive)" onClick={() => confirmDelete(r)} />
                </Tooltip>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  // Columns for the expanded master row's variant sub-table — a self-headed nested table (its own
  // headers), matching the Stock page's variant breakdown. Rendered inside TwDataTable's tinted,
  // accent-railed expanded strip, so variants read as a distinct sub-section of their product.
  const variantColumns = [
    {
      key: 'variant', title: 'Variant', width: 160,
      render: (_: any, v: any) => (
        <span className="inline-flex items-center gap-1.5">
          <Pill color="green">{Number(v.packSize) > 0 ? fmtUom(v.packSize, v.uom) : (v.variantLabel || v.uom)}</Pill>
          {v.isActive === false && <Pill>Inactive</Pill>}
        </span>
      ),
    },
    {
      key: 'sku', title: 'SKU', dataIndex: 'sku', width: 180,
      render: (val: string) => <span style={{ fontFamily: MONO, fontSize: 13 }}>{val}</span>,
    },
    {
      key: 'pack', title: 'Pack', dataIndex: 'packSize', width: 150,
      render: (_: any, v: any) => <span style={NUM}>{Number(v.packSize) > 0 ? fmtUom(v.packSize, v.uom) : v.uom}</span>,
    },
    {
      key: 'mrp', title: 'MRP', dataIndex: 'mrp', width: 155,
      render: (val: any, v: any) => (
        <div style={{ ...NUM, color: token.colorTextSecondary, lineHeight: 1.25 }}>
          {inr(val)}
          {perUnit(val, v.packSize, v.uom) && <div style={{ fontSize: 11, color: token.colorTextTertiary }}>{perUnit(val, v.packSize, v.uom)}</div>}
        </div>
      ),
    },
    {
      key: 'price', title: 'Dealer Price', dataIndex: 'price', width: 185,
      render: (val: any, v: any) => (
        <div style={{ ...NUM, lineHeight: 1.25 }}>
          <b>{inr(val)}</b>
          {perUnit(val, v.packSize, v.uom) && <div style={{ fontSize: 11, fontWeight: 400, color: token.colorTextTertiary }}>{perUnit(val, v.packSize, v.uom)}</div>}
        </div>
      ),
    },
    {
      // Same chip set as the parent table's Stock column, so it needs the same room to stay on one line.
      key: 'stock', title: 'Stock', dataIndex: 'stock', width: 520,
      render: (s: any, v: any) => {
        const whs = Object.entries(s?.warehouses ?? {});
        return (
          <span style={NUM} className="inline-flex flex-wrap items-center gap-1">
            {whs.slice(0, 3).map(([wh, q]) => <Pill key={wh}>{wh}: {String(q)}</Pill>)}
            {whs.length > 3 && <Pill>+{whs.length - 3}</Pill>}
            <Tooltip label={`${fmtPackQty(s?.total ?? 0, v.packSize, v.uom)} in stock`}><span className="inline-flex">{stockTag(s?.total ?? 0)}</span></Tooltip>
          </span>
        );
      },
    },
    {
      key: 'actions', title: '', align: 'right' as const, width: 150,
      render: (_: any, v: any) => (
        <Space wrap size="small" onClick={(e: any) => e.stopPropagation()}>
          {canEdit && (
            <Tooltip label="Edit variant (this pack only)">
              <Button variant="text" size="sm" icon={<Pencil size={16} />} aria-label="Edit variant" onClick={() => { setEditing({ ...v, _editMode: 'variant' }); setFormOpen(true); }} />
            </Tooltip>
          )}
          {canCreate && (
            <Tooltip label="Add variant (link to this product)">
              <Button variant="text" size="sm" icon={<GitBranch size={16} />} aria-label="Add variant" onClick={() => setVariantFor(v)} />
            </Tooltip>
          )}
          {canQr && (
            <Tooltip label="Generate QR">
              <Button variant="text" size="sm" icon={<QrCode size={16} />} aria-label="Generate QR" onClick={() => setQrFor(v)} />
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip label="Delete (archive)">
              <Button variant="text" size="sm" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} aria-label="Delete (archive)" onClick={() => confirmDelete(v)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Products · Catalog with live stock</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Space wrap size="small">
            {canCreate && (
              <Button variant="primary" size="sm" icon={<Plus size={16} />}
                onClick={() => { setEditing(null); setFormOpen(true); }}>
                Add product
              </Button>
            )}
            {/*
              * One menu instead of three buttons labelled Export / CSV / JSON.
              *
              * Those labels named FILE FORMATS, not actions, and two of them opened import/export
              * modals rather than downloading anything — so the row read as three ways to export when
              * it was really "download a document" plus two round-trip data tools. Grouping by intent
              * says what each one is FOR, and the formats become a detail inside.
              */}
            {(canExport || canCreate || canEdit) && (
              <Dropdown
                align="end"
                panel={
                  <>
                    {canExport && <MenuLabel>Download</MenuLabel>}
                    {canExport && (
                      <MenuItem icon={<Download size={16} />} onClick={() => setExportOpen(true)}>
                        Catalogue — PDF or Excel
                      </MenuItem>
                    )}
                    {(canExport || canCreate || canEdit) && <MenuDivider />}
                    <MenuLabel>Data files (import &amp; export)</MenuLabel>
                    {(canExport || canCreate) && (
                      <MenuItem icon={<FileSpreadsheet size={16} />} onClick={() => setCsvModalOpen(true)}>
                        Product data — CSV
                      </MenuItem>
                    )}
                    {(canExport || canEdit) && (
                      <MenuItem
                        icon={<FileJson size={16} />}
                        onClick={() => { setDetailsFile(null); setPendingDetails(null); setDetailsImportOpen(true); }}
                      >
                        Page content — JSON
                      </MenuItem>
                    )}
                  </>
                }
              >
                {/* A two-way hub: a lone download arrow announced only half of what the menu does. */}
                <Button variant="default" size="sm" icon={<ArrowUpDown size={16} />}>
                  Import / export
                </Button>
              </Dropdown>
            )}
          </Space>
        </div>
      </div>

      {/* Summary tiles — the shared ui/StatCard, not three hand-rolled buttons. These predated the
          component and had drifted: a bare label + figure, an accent expressed as a whole-border
          colour swap when selected, and one of the three with no selected state at all. Routing
          them through StatCard gives them the same tone wash, icon chip, elevation and pressed
          state every other register's KPI band already has. Values, handlers and filter semantics
          are unchanged. */}
      <div className="mb-4 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard
          label="Total SKUs"
          value={totalSkus}
          tone="brand"
          icon={<Boxes className="size-4" />}
          active={stockFilter === 'all'}
          onClick={() => setStockFilter('all')}
        />
        <StatCard
          label="Out of stock"
          value={outOfStock}
          tone="danger"
          icon={<PackageX className="size-4" />}
          active={stockFilter === 'out'}
          onClick={() => setStockFilter((s) => (s === 'out' ? 'all' : 'out'))}
        />
        {canValuation && (
          <StatCard
            label="Total valuation"
            value={inr(valuation?.totalValue ?? 0)}
            hint="On-hand value, at cost"
            tone="sky"
            icon={<IndianRupee className="size-4" />}
            onClick={() => navigate('/inventory/valuation')}
          />
        )}
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">{/* filter controls (Antd) */}
          <Input
            placeholder="Search SKU, name, HSN" aria-label="Search SKU, name, HSN"
            prefix={<Filter size={16} style={{ color: token.colorTextTertiary }} />}
            className="w-60"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Group code"
            style={{ minWidth: 180 }}
            maxTagCount={1}
            value={groupFilter}
            onChange={setGroupFilter}
            options={groupOptions}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="UoM"
            style={{ minWidth: 130 }}
            maxTagCount={1}
            value={uomFilter}
            onChange={setUomFilter}
            options={uomOptions}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Warehouse"
            style={{ minWidth: 160 }}
            maxTagCount={1}
            value={whFilter}
            onChange={setWhFilter}
            options={whOptions}
          />
          <Select
            style={{ width: 150 }}
            value={stockFilter}
            onChange={setStockFilter}
            options={[
              { value: 'all', label: 'Any stock' },
              { value: 'in', label: 'In stock' },
              { value: 'out', label: 'Out of stock' },
            ]}
          />
          <Select
            style={{ width: 150 }}
            value={typeFilter}
            onChange={setTypeFilter}
            options={[
              { value: 'all', label: 'All products' },
              { value: 'single', label: 'Single pack' },
              { value: 'multi', label: 'Multi-variant' },
            ]}
          />
          <Select
            style={{ width: 150 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'true', label: 'Active only' },
              { value: 'false', label: 'Inactive only' },
              { value: 'all', label: 'All statuses' },
            ]}
          />
          {hasPriceFilter && (
            <Popover
              trigger="click"
              title="Dealer price range"
              content={
                <div style={{ width: 240 }}>
                  <Slider
                    range
                    min={priceBounds[0]}
                    max={priceBounds[1]}
                    value={priceRange ?? priceBounds}
                    tooltip={{ formatter: (v) => inr(v) }}
                    onChange={(v) => setPriceRange(v as [number, number])}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorTextSecondary }}>
                    <span>{inr((priceRange ?? priceBounds)[0])}</span>
                    <span>{inr((priceRange ?? priceBounds)[1])}</span>
                  </div>
                  {priceRange && (
                    <Button variant="text" size="sm" style={{ padding: 0, marginTop: 4 }} onClick={() => setPriceRange(null)}>
                      Reset price
                    </Button>
                  )}
                </div>
              }
            >
              <Button variant="default">
                {priceRange ? `${inr(priceRange[0])} – ${inr(priceRange[1])}` : 'Price'}
              </Button>
            </Popover>
          )}
          {activeFilters > 0 && (
            <FilterResetButton size="md" count={activeFilters} onClick={clearFilters} />
          )}
      </div>

      <TwDataTable
        storageKey="products-list-v3"
        rowKey="productId"
        loading={isLoading || isFetching}
        dataSource={groupedRows}
        columns={columns}
        expandable={{
          expandRowByClick: true,
          rowExpandable: (r: any) => r._isGroup === true,
          // Variants render as a self-headed sub-table inside the shared tinted/accent-railed
          // expanded strip (the Stock-page look) instead of column-reusing ↳ child rows.
          expandedRowRender: (r: any) => (
            <Table
              size="small"
              pagination={false}
              rowKey="productId"
              dataSource={r._variants ?? []}
              columns={variantColumns as any}
            />
          ),
        }}
        onRow={(r: any) => ({
          onClick: (!r._isGroup && canEdit) ? () => { setEditing({ ...r, _editMode: 'full' }); setFormOpen(true); } : undefined,
          // A multi-variant product reads as ONE bounded block: the master row is a shaded header with
          // a strong divider above it (separates one product from the next); its variants live in the
          // expanded sub-section below. Standalone single products get no tint, staying distinct.
          className: r._isGroup ? 'bg-surface-2 [&>td]:border-t-2 [&>td]:border-t-line-strong' : '',
        })}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        emptyText={activeFilters > 0 ? 'No products match these filters' : 'No products'}
        toolbarLeft={
          <span className="text-sm text-muted">
            {rows.length === totalSkus
              ? `${totalSkus} SKU${totalSkus === 1 ? '' : 's'}`
              : `${rows.length} of ${totalSkus} SKUs`}
            {activeFilters > 0 ? ` · ${activeFilters} filter${activeFilters === 1 ? '' : 's'}` : ''}
          </span>
        }
      />

      <ProductFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        record={editing}
      />

      <ExportDialog

        open={exportOpen}

        onClose={() => setExportOpen(false)}

        title="Export product catalogue"

        description="A catalogue to share, or a price list to work from."
        baseUrl="/products/export"
        hidePeriod
        detailToggle={{
          summaryLabel: 'Price list',
          fullLabel: 'Catalogue',
          fullHint: 'A card per product — cover image, tagline, what it is for, and the price. The document you hand to a dealer.',
          summaryHint: 'A compact table of SKU, pack, HSN, MRP and dealer price. For pricing work, not for sharing.',
        }}
      />

      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['catalog-with-stock'] })}
        kind="products"
        entityPlural="products"
        columns="sku, name, hsnCode, uomCode, packSize, mrp, dealerPrice, moq, reorderLevel"
      />

      <AddVariantModal master={variantFor} onClose={() => setVariantFor(null)} />

      <ProductDetailModal product={viewFor} onClose={() => setViewFor(null)} />

      <Modal
        open={!!qrFor}
        onOpenChange={(o) => { if (!o) setQrFor(null); }}
        title={`Product QR · ${qrFor?.sku ?? ''}`}
        footer={[
          <Button key="close" variant="default" onClick={() => setQrFor(null)}>Close</Button>,
          qr?.dataUrl ? (
            <a key="dl" href={qr.dataUrl} download={`${qrFor?.sku ?? 'product'}-qr.png`}>
              <Button variant="primary">Download PNG</Button>
            </a>
          ) : null,
        ]}
      >
        {qrLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
        ) : qr ? (
          <div style={{ textAlign: 'center' }}>
            <img src={qr.dataUrl} alt="Product QR" style={{ width: 240, height: 240 }} />
            <pre style={{
              textAlign: 'left', background: token.colorFillQuaternary, padding: 12,
              borderRadius: 8, marginTop: 12, whiteSpace: 'pre-wrap', marginBottom: 0,
            }}>{qr.text}</pre>
          </div>
        ) : null}
      </Modal>

      {/* CSV hub — base commercial product data */}
      <Modal
        open={csvModalOpen}
        onOpenChange={(o) => { if (!o) setCsvModalOpen(false); }}
        title="Products · CSV (base data)"
        footer={[<Button key="close" variant="default" onClick={() => setCsvModalOpen(false)}>Close</Button>]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0 }}>
            The <b>commercial</b> product data — SKU, name, HSN, UoM, pack size, MRP, dealer price, MOQ and reorder
            level. (The rich page content lives under <b>JSON</b>.)
          </p>
          {canExport && (
            <div>
              <Button variant="default" icon={<Download size={16} />} onClick={() => downloadCsv('/products/export.csv', 'products.csv')}>
                Export products (CSV)
              </Button>
            </div>
          )}
          {canCreate && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 14 }}>
              <div style={{ fontWeight: 500 }}>Import products</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Button variant="default" icon={<UploadIcon size={16} />} onClick={() => { setCsvModalOpen(false); setImportOpen(true); }}>
                  Import products (CSV)
                </Button>
                <Typography.Link
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}
                  onClick={() => downloadCsv('/imports/templates/products.csv', 'products-template.csv').catch(() => message.error('Template unavailable'))}
                >
                  <Download size={16} /> Download CSV template
                </Typography.Link>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* JSON hub — rich product-detail content, with template + drag-and-drop import */}
      <Modal
        open={detailsImportOpen}
        onOpenChange={(o) => { if (!o) resetDetailsImport(); }}
        title="Product details · JSON (rich content)"
        footer={[
          <Button key="close" variant="default" onClick={resetDetailsImport}>Close</Button>,
          canEdit ? (
            <Button key="import" variant="primary" disabled={!pendingDetails?.length} loading={importingDetails} onClick={doDetailsImport}>
              {pendingDetails?.length ? `Import ${pendingDetails.length}` : 'Import'}
            </Button>
          ) : null,
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0 }}>
            The <b>marketing detail</b> content — category, crops, benefit/application tags, tagline, summary, highlights
            and the accordion sections (composition, dosage table, specs…). Base product data (SKU, price, stock) is
            managed under <b>CSV</b> and is never touched here.
          </p>
          {canExport && (
            <div>
              <Button
                variant="default"
                icon={<Download size={16} />}
                onClick={() => downloadJson('/products/details/export.json', 'product-details.json').catch(() => message.error('Export failed'))}
              >
                Export details (JSON)
              </Button>
            </div>
          )}
          {canEdit && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 14 }}>
              <div style={{ fontWeight: 500 }}>Import details</div>
              <div style={{ fontSize: 13 }}>
                Upload an edited export, keyed by <code>groupCode</code> (unknown group codes are skipped). New to the
                format?{' '}
                <Typography.Link
                  style={{ cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => downloadJson('/products/details/template.json', 'product-details-template.json').catch(() => message.error('Template unavailable'))}
                >
                  Download JSON template
                </Typography.Link>.
              </div>
              <Upload.Dragger
                accept=".json,application/json"
                multiple={false}
                maxCount={1}
                beforeUpload={(f) => { void parseDetailsFile(f as unknown as File); return false; }}
                onRemove={() => { setDetailsFile(null); setPendingDetails(null); }}
                fileList={detailsFile ? ([{ uid: '1', name: detailsFile.name }] as any) : []}
              >
                <p style={{ margin: 0 }}><FileUp size={24} style={{ opacity: 0.6 }} /></p>
                <p style={{ margin: '8px 0 2px', fontWeight: 500 }}>Click or drag a .json file here</p>
                <p style={{ margin: 0, fontSize: 12, opacity: 0.65 }}>A product-details export or the template — one file</p>
              </Upload.Dragger>
              {pendingDetails?.length ? (
                <p style={{ margin: 0, color: token.colorSuccess }}>✓ {pendingDetails.length} product master(s) ready to import.</p>
              ) : detailsFile ? (
                <p style={{ margin: 0, color: token.colorError }}>Couldn’t read any records from this file.</p>
              ) : null}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
